import H5 from '@sdk/components/ui/typography/H5';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text12 from '@sdk/components/ui/typography/Text12';
import gettext from '@sdk/lang/gettext';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import TextField from '@sdk/components/forms/TextField';
import FieldLabel from '@sdk/components/forms/components/FieldLabel';
import Switcher from '@sdk/components/forms/Switcher';
import DropOrBrowseFiles from '@sdk/components/ui/DropOrBrowseFiles/DropOrBrowseFiles';
import PickerField from '@sdk/components/forms/PickerField';
import FooterSaveActions from '@sdk/components/ui/FooterSaveActions';
import useAuth from '@sdk/authentication/useAuth';
import getRequestUrl from '@sdk/api/utils/getRequestUrl';
import getNowDate from '@sdk/utils/getNowDate';
import type { User } from '@sdk/users/types/User';
import type { DocumentData } from '@sdk/users/types/DocumentData';

import { OutputDateFormatType } from '@web/forms/components/datePicker/datePicker.types';
import DatePickerField from '@web/forms/components/datePicker/DatePickerField/DatePickerField';
import CloseSvg from '@web/assets/svg/UI/close.svg';
import ExpiryBanner from '@web/users/documents/ExpiryBanner/ExpiryBanner';
import FormSubmit from '@web/components/FormSubmit';
import DocumentPreview from '@web/users/documents/DocumentPreview/DocumentPreview';
import {
  NewDocumentContainer,
  FieldContainer,
  FileRow,
  RemoveFileBtn,
  Anchor,
} from '@web/users/documents/DocumentDetails/documentDetails.styles';
import {
  SharedVisibilityOption,
  useDocumentDetails,
} from '@web/users/documents/DocumentDetails/documentDetails.hooks';

type Props = {
  user: User;
  documentToEdit: DocumentData;
  goBackToList: () => void;
};

const DocumentDetails = ({ user, goBackToList, documentToEdit }: Props) => {
  const { currentUser } = useAuth();
  const {
    formFields,
    submitErrors,
    dropzoneState,
    selectedFile,
    clearFile,
    visibilityOptions,
    isValid,
    onSaveHandle,
    isFileUpdating,
    onFileClick,
    downloadFormRef,
    documentPreviewRef,
    isFormDirty,
    discardChanges,
  } = useDocumentDetails(user, goBackToList, documentToEdit);

  const showFormErrors = Boolean(submitErrors?.length);
  const showSelectedFile = Boolean(selectedFile || (documentToEdit?.fileName && !isFileUpdating));
  const showBrowseFile = Boolean(
    currentUser.isManager &&
      !selectedFile &&
      (!showSelectedFile || documentToEdit?.isExpiringSoon || documentToEdit?.isExpired)
  );

  const showCancelSave = isFormDirty || selectedFile || isFileUpdating;
  const todayDate = getNowDate();

  return (
    <NewDocumentContainer>
      {!!documentToEdit?.isExpiringSoon && (
        <ExpiryBanner
          type="warning"
          mTop={20}
          mBottom={-20}
          text={
            currentUser.isManager
              ? gettext(
                  'This document is expiring soon.\nUpload a new file and update the expiration date.'
                )
              : gettext('This document is expiring soon.')
          }
        />
      )}
      {!!documentToEdit?.isExpired && (
        <ExpiryBanner
          type="danger"
          mTop={20}
          mBottom={-20}
          text={
            currentUser.isManager
              ? gettext(
                  'This document is expired. Upload a new file and\nupdate the expiration date, or delete this document.'
                )
              : gettext('This document is expired.')
          }
        />
      )}
      <Row mTop={40} mBottom={10}>
        <H5 themeColor={ThemeColorType.primary}>{gettext('Document details')}</H5>
      </Row>
      <Row mBottom={20}>
        <HorizontalSeparator />
      </Row>
      {showSelectedFile && (
        <Column mBottom={20}>
          <FieldLabel label={gettext('Attached file')} />
          <FileRow disabled={!currentUser.isManager}>
            {selectedFile ? (
              <Text14 themeColor={ThemeColorType.primary}>{selectedFile.name}</Text14>
            ) : (
              <Anchor onClick={onFileClick}>{documentToEdit?.fileName}</Anchor>
            )}
            {currentUser.isManager && (
              <RemoveFileBtn onClick={clearFile}>
                <CloseSvg />
              </RemoveFileBtn>
            )}
          </FileRow>
        </Column>
      )}
      {showBrowseFile && (
        <Row mBottom={20}>
          <DropOrBrowseFiles
            isInvalid={showFormErrors && !selectedFile}
            dropzoneState={dropzoneState}
            dropMessage={
              documentToEdit?.fileName
                ? gettext('Drop your document to replace')
                : gettext('Drop your document here')
            }
          />
        </Row>
      )}
      <Row>
        <FieldContainer mRight={5}>
          <TextField
            value={formFields.name.value}
            onChange={formFields.name.onChange}
            onBlur={formFields.name.onBlur}
            invalid={!!(showFormErrors && formFields.name.error)}
            placeholder={gettext('Add a name')}
            label={gettext('Document name')}
            id="document-name"
            error={(showFormErrors && formFields.name.error) || ''}
            disabled={!currentUser.isManager}
          />
        </FieldContainer>
        <FieldContainer mLeft={5}>
          <DatePickerField
            clearable
            outputFormat={OutputDateFormatType.ddMMMyyyy}
            placeholder={gettext('Add date')}
            date={formFields.expirationDate.value}
            onDateChange={formFields.expirationDate.onChange}
            onBlur={formFields.expirationDate.onBlur}
            invalid={!!(showFormErrors && formFields.expirationDate.error)}
            label={gettext('Expiration date')}
            id="expiration-date"
            error={(showFormErrors && formFields.expirationDate.error) || ''}
            disabled={!currentUser.isManager}
            minDate={todayDate}
          />
        </FieldContainer>
      </Row>
      <Row mTop={20}>
        <TextField
          value={formFields.description.value}
          onChange={formFields.description.onChange}
          onBlur={formFields.description.onBlur}
          invalid={!!(showFormErrors && formFields.description.error)}
          placeholder={gettext('Add description for the document')}
          label={gettext('Description')}
          id="description"
          error={(showFormErrors && formFields.description.error) || ''}
          disabled={!currentUser.isManager}
        />
      </Row>
      {currentUser.isManager && (
        <>
          <Row mTop={20} mBottom={10}>
            <H5 themeColor={ThemeColorType.primary}>{gettext('Settings')}</H5>
          </Row>
          <HorizontalSeparator />
          <Row mTop={20} alignItems="center">
            <Column>
              <Text14 semiBold themeColor={ThemeColorType.gray}>
                {gettext('Shared access')}
              </Text14>
              <Text12 themeColor={ThemeColorType.gray}>
                {gettext('Make this document visible to others.')}
              </Text12>
            </Column>
            <Column mLeft={'auto'}>
              <Switcher
                small
                value={formFields.isShared.value}
                onChange={formFields.isShared.onChange}
              />
            </Column>
          </Row>
          {formFields.isShared.value && (
            <Row mTop={20} alignItems="center">
              <Column mRight={80}>
                <Text14 themeColor={ThemeColorType.gray}>{gettext('Make it visible:')}</Text14>
              </Column>
              <PickerField
                options={visibilityOptions}
                value={formFields.sharedVisibilityOption.value}
                onChange={(value: string) =>
                  formFields.sharedVisibilityOption.onChange(value as SharedVisibilityOption)
                }
              />
            </Row>
          )}
          {!!formFields.expirationDate.value && (
            <Row mTop={20} alignItems="center">
              <Column>
                <Text14 semiBold themeColor={ThemeColorType.gray}>
                  {gettext('Expiration notifications')}
                </Text14>
                <Text12 themeColor={ThemeColorType.gray}>
                  {gettext('Send a reminder when the expiration date approaches.')}
                </Text12>
              </Column>
              <Column mLeft={'auto'}>
                <Switcher
                  small
                  value={formFields.expirationNotificationsEnabled.value}
                  onChange={formFields.expirationNotificationsEnabled.onChange}
                />
              </Column>
            </Row>
          )}
          {!!documentToEdit && (
            <DocumentPreview document={documentToEdit} ref={documentPreviewRef} />
          )}
          {showCancelSave && (
            <FooterSaveActions
              onSaveClick={onSaveHandle}
              onCancelClick={discardChanges}
              isValid={isValid}
              allowSaveCallbackIfInvalid={true}
            />
          )}
        </>
      )}
      {!!documentToEdit && (
        <FormSubmit
          formRef={downloadFormRef}
          url={getRequestUrl(
            `users/${documentToEdit.user.id}/documents/${documentToEdit.id}/download`,
            {}
          )}
        />
      )}
    </NewDocumentContainer>
  );
};

export default DocumentDetails;
